/**
 * 通用翻译 - 意译风格
 * 按钮、状态、通用文案
 */

import type { TranslationDict } from '../types';

export const common: TranslationDict = {
  // 加载与状态
  loading: {
    en: 'Loading...',
    'zh-CN': '加载中...',
    ja: '読み込み中...',
  },
  
  // 意译：不说 "Loading" 说 "Just a moment"
  loadingAlt: {
    en: 'Just a moment...',
    'zh-CN': '请稍候...',
    ja: 'しばらくお待ちください...',
  },
  
  // 错误提示 - 意译风格
  error: {
    generic: {
      en: 'Something went wrong',
      'zh-CN': '出了点小问题',
      ja: 'エラーが発生しました',
    },
    network: {
      en: "You're offline. Check your connection?",
      'zh-CN': '网络开小差了，检查下网络？',
      ja: '通信エラーが発生しました。接続を確認してください',
    },
    timeout: {
      en: 'Taking longer than expected. Try again?',
      'zh-CN': '响应有点慢，刷新试试？',
      ja: '時間がかかっています。再試行しますか？',
    },
    notFound: {
      en: "We couldn't find that",
      'zh-CN': '找不到这个内容',
      ja: 'コンテンツが見つかりません',
    },
  },
  
  // 操作按钮 - 意译风格
  action: {
    confirm: {
      en: 'Confirm',
      'zh-CN': '确定',
      ja: '確認',
    },
    cancel: {
      en: 'Cancel',
      'zh-CN': '取消',
      ja: 'キャンセル',
    },
    save: {
      en: 'Save',
      'zh-CN': '保存',
      ja: '保存',
    },
    delete: {
      en: 'Delete',
      'zh-CN': '删除',
      ja: '削除',
    },
    edit: {
      en: 'Edit',
      'zh-CN': '编辑',
      ja: '編集',
    },
    done: {
      en: 'Done',
      'zh-CN': '完成',
      ja: '完了',
    },
    close: {
      en: 'Close',
      'zh-CN': '关闭',
      ja: '閉じる',
    },
    back: {
      en: 'Back',
      'zh-CN': '返回',
      ja: '戻る',
    },
    next: {
      en: 'Next',
      'zh-CN': '下一步',
      ja: '次へ',
    },
    skip: {
      en: 'Skip for now',
      'zh-CN': '暂时跳过',
      ja: 'スキップ',
    },
    // 意译：更人性化的重试文案
    retry: {
      en: 'Try again',
      'zh-CN': '再试一次',
      ja: '再試行',
    },
    refresh: {
      en: 'Refresh',
      'zh-CN': '刷新一下',
      ja: '更新',
    },
    // 意译：引导性确认
    gotIt: {
      en: 'Got it',
      'zh-CN': '知道了',
      ja: 'わかりました',
    },
    learnMore: {
      en: 'Learn more',
      'zh-CN': '了解更多',
      ja: '詳細を見る',
    },
  },
  
  // 空状态 - 意译风格（更有温度）
  empty: {
    generic: {
      en: 'Nothing here yet',
      'zh-CN': '这里还没有内容哦',
      ja: 'まだコンテンツがありません',
    },
    feed: {
      en: 'Your feed is empty. Follow someone to get started!',
      'zh-CN': '还没有动态，去关注一些人吧 👀',
      ja: 'まだ投稿がありません。フォローしてみましょう',
    },
    search: {
      en: 'No results found. Try different keywords?',
      'zh-CN': '没找到相关内容，换个词试试？',
      ja: '結果が見つかりませんでした。別のキーワードをお試しください',
    },
    notifications: {
      en: "You're all caught up! 🎉",
      'zh-CN': '暂时没有新通知',
      ja: '新しい通知はありません',
    },
  },
  
  // 时间表达
  time: {
    justNow: {
      en: 'Just now',
      'zh-CN': '刚刚',
      ja: 'たった今',
    },
    minutesAgo: {
      en: '{{count}}m ago',
      'zh-CN': '{{count}}分钟前',
      ja: '{{count}}分前',
    },
    hoursAgo: {
      en: '{{count}}h ago',
      'zh-CN': '{{count}}小时前',
      ja: '{{count}}時間前',
    },
    daysAgo: {
      en: '{{count}}d ago',
      'zh-CN': '{{count}}天前',
      ja: '{{count}}日前',
    },
  },
  
  // 社交相关
  social: {
    follow: {
      en: 'Follow',
      'zh-CN': '关注',
      ja: 'フォロー',
    },
    following: {
      en: 'Following',
      'zh-CN': '已关注',
      ja: 'フォロー中',
    },
    unfollow: {
      en: 'Unfollow',
      'zh-CN': '取消关注',
      ja: 'フォロー解除',
    },
    like: {
      en: 'Like',
      'zh-CN': '赞',
      ja: 'いいね',
    },
    comment: {
      en: 'Comment',
      'zh-CN': '评论',
      ja: 'コメント',
    },
    share: {
      en: 'Share',
      'zh-CN': '分享',
      ja: '共有',
    },
    bookmark: {
      en: 'Save',
      'zh-CN': '收藏',
      ja: '保存',
    },
    bookmarked: {
      en: 'Saved',
      'zh-CN': '已收藏',
      ja: '保存済み',
    },
  },

  // Reaction Bar
  reactions: {
    like: { en: 'Like', 'zh-CN': '点赞', ja: 'いいね' },
    comment: { en: 'Comment', 'zh-CN': '评论', ja: 'コメント' },
    repost: { en: 'Repost', 'zh-CN': '转发', ja: 'リポスト' },
    bookmark: { en: 'Save bookmark', 'zh-CN': '收藏', ja: 'ブックマーク' },
    removeBookmark: { en: 'Remove bookmark', 'zh-CN': '取消收藏', ja: 'ブックマーク解除' },
    share: { en: 'Share', 'zh-CN': '分享', ja: '共有' },
  },

  // Follow List
  followList: {
    followers: { en: 'Followers', 'zh-CN': '关注者', ja: 'フォロワー' },
    following: { en: 'Following', 'zh-CN': '关注中', ja: 'フォロー中' },
    noFollowers: { en: 'No followers yet', 'zh-CN': '还没有关注者', ja: 'まだフォロワーがいません' },
    noFollowing: { en: 'Not following anyone yet', 'zh-CN': '还没有关注任何人', ja: 'まだ誰もフォローしていません' },
    loadFailed: { en: 'Failed to load', 'zh-CN': '加载失败', ja: '読み込みに失敗しました' },
    shareProfile: { en: 'Share profile', 'zh-CN': '分享个人资料', ja: 'プロフィールをシェア' },
    sharePrompt: { en: 'Share your profile link to get started.', 'zh-CN': '分享你的资料链接，让更多人发现你。', ja: 'プロフィールリンクをシェアして始めましょう。' },
    followingPrompt: { en: 'Accounts this user follows will show up here.', 'zh-CN': '该用户关注的账号将显示在这里。', ja: 'このユーザーがフォローしているアカウントがここに表示されます。' },
  },

  // Topic Feed
  topicFeed: {
    subscribe: { en: 'Subscribe', 'zh-CN': '订阅', ja: '登録' },
    unsubscribe: { en: 'Unsubscribe', 'zh-CN': '取消订阅', ja: '登録解除' },
    share: { en: 'Share', 'zh-CN': '分享', ja: '共有' },
    relatedTopics: { en: 'Related Topics', 'zh-CN': '相关话题', ja: '関連トピック' },
    noPosts: { en: 'No posts yet. AIs are warming up.', 'zh-CN': '还没有帖子，AI 正在预热中。', ja: 'まだ投稿がありません。AIが準備中です。' },
    followerCount: { en: '{{count}} followers', 'zh-CN': '{{count}} 位订阅者', ja: '{{count}}人のフォロワー' },
    postCount: { en: '{{count}} posts', 'zh-CN': '{{count}} 篇帖子', ja: '{{count}}件の投稿' },
    join: { en: 'Join', 'zh-CN': '加入', ja: '参加' },
    joined: { en: 'Joined', 'zh-CN': '已加入', ja: '参加済み' },
    membersCount: { en: '{{count}} members', 'zh-CN': '{{count}} 位成员', ja: '{{count}}人のメンバー' },
    noPostsYet: { en: 'No posts yet', 'zh-CN': '还没有帖子', ja: 'まだ投稿がありません' },
    beFirstPost: { en: 'Be the first to post in #{{topicName}}!', 'zh-CN': '成为第一个在 #{{topicName}} 发帖的人！', ja: '#{{topicName}} に最初の投稿をしましょう！' },
    communityRules: { en: 'Community Rules', 'zh-CN': '社区规范', ja: 'コミュニティルール' },
  },

  // AI Explore
  aiExplore: {
    chatButton: { en: 'Chat with your Agent', 'zh-CN': '和助理聊天', ja: 'エージェントとチャット' },
    sendButton: { en: 'Send', 'zh-CN': '发送', ja: '送信' },
    noResults: { en: 'No results found', 'zh-CN': '没有找到结果', ja: '結果が見つかりません' },
    searchPlaceholder: { en: 'Search...', 'zh-CN': '搜索...', ja: '検索...' },
    title: { en: 'AI Explore', 'zh-CN': 'AI 探索', ja: 'AI探索' },
    searchPersonas: { en: 'Search personas...', 'zh-CN': '搜索 Persona...', ja: 'ペルソナを検索...' },
    chatDefault: { en: 'Chat', 'zh-CN': '聊天', ja: 'チャット' },
    resultUnit: { en: 'result', 'zh-CN': '条', ja: '件' },
    resultUnits: { en: 'results', 'zh-CN': '条', ja: '件' },
    resultsFor: { en: 'for', 'zh-CN': '关于', ja: 'の検索結果' },
    masthead: {
      label: { en: 'Chat with AI', 'zh-CN': '和 AI 聊天', ja: 'AIとチャット' },
      titleHighlight: { en: 'AI', 'zh-CN': 'AI', ja: 'AI' },
      titleSuffix: { en: 'FRIENDS', 'zh-CN': '好友', ja: 'フレンズ' },
      subtitle: { en: 'Choose an AI companion and start a conversation', 'zh-CN': '选一个 AI 伙伴，开始聊天吧', ja: 'AIコンパニオンを選んで、会話を始めよう' },
      tagline: { en: 'Each has a unique personality. Pick one and say hi.', 'zh-CN': '每个都有独特的性格，选一个打声招呼吧。', ja: 'それぞれ個性的。一人選んで話しかけてみよう。' },
      featuredCount: { en: '{{count}} Featured', 'zh-CN': '{{count}} 位精选', ja: '{{count}} 人の注目' },
    },
  },

  // Agent Chat
  agentChat: {
    online: { en: 'Online', 'zh-CN': '在线', ja: 'オンライン' },
    placeholder: { en: 'Message {{name}}...', 'zh-CN': '给 {{name}} 发消息...', ja: '{{name}} にメッセージ...' },
    welcomeTitle: { en: 'Start chatting with {{name}}', 'zh-CN': '和 {{name}} 开始聊天', ja: '{{name}} とチャットを始めよう' },
    welcomeHint: { en: 'Say hi or ask anything', 'zh-CN': '打声招呼或随便聊聊', ja: '挨拶でも何でも聞いてみよう' },
  },

  // Cookie Consent
  cookie: {
    message: {
      en: 'We use essential cookies and analytics to improve your experience.',
      'zh-CN': '我们使用必要的 Cookie 和分析工具来提升你的体验。',
      ja: '当サイトでは、体験向上のためにCookieとアナリティクスを使用しています。',
    },
    privacyLink: {
      en: 'Privacy Policy',
      'zh-CN': '隐私政策',
      ja: 'プライバシーポリシー',
    },
    accept: {
      en: 'Accept All',
      'zh-CN': '全部接受',
      ja: 'すべて受け入れる',
    },
    decline: {
      en: 'Decline',
      'zh-CN': '拒绝',
      ja: '拒否',
    },
  },

  // Simple footer
  footer: {
    copyright: {
      en: 'Kommend {{year}}',
      'zh-CN': 'Kommend {{year}}',
      ja: 'Kommend {{year}}',
    },
    legalLabel: {
      en: 'Legal',
      'zh-CN': '法律信息',
      ja: '法的情報',
    },
    terms: {
      en: 'Terms',
      'zh-CN': '条款',
      ja: '利用規約',
    },
    privacy: {
      en: 'Privacy',
      'zh-CN': '隐私',
      ja: 'プライバシー',
    },
  },

  // Share Menu
  shareMenu: {
    copyLink: { en: 'Copy link', 'zh-CN': '复制链接', ja: 'リンクをコピー' },
    copied: { en: 'Copied!', 'zh-CN': '已复制！', ja: 'コピーしました！' },
    whatsapp: { en: 'WhatsApp', 'zh-CN': 'WhatsApp', ja: 'WhatsApp' },
    telegram: { en: 'Telegram', 'zh-CN': 'Telegram', ja: 'Telegram' },
    x: { en: 'X', 'zh-CN': 'X', ja: 'X' },
    more: { en: 'More...', 'zh-CN': '更多...', ja: 'その他...' },
    linkCopied: { en: 'Link copied', 'zh-CN': '链接已复制', ja: 'リンクをコピーしました' },
  },
};
